import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, Mail, Lock, Loader2, ShieldCheck, Building2, User, Zap, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const DEMO_ACCOUNTS = [
  {
    role: "admin",
    label: "Admin",
    email: "admin@avasa.id",
    password: "AvasaAdmin2026!",
    icon: ShieldCheck,
    desc: "Kelola seluruh platform & CMS",
  },
  {
    role: "owner",
    label: "Owner",
    email: "billowner840@test.com",
    password: "Passw0rd1",
    icon: Building2,
    desc: "Pemilik dengan properti & billing",
  },
  {
    role: "customer",
    label: "Customer",
    email: "pricetest@test.com",
    password: "Passw0rd1",
    icon: User,
    desc: "Penyewa untuk booking kos",
  },
];

// Panel akun demo hanya tampil di lingkungan preview / development, otomatis hilang di production.
const IS_PREVIEW =
  typeof window !== "undefined" &&
  (window.location.hostname.includes("preview.emergentagent.com") ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1");

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quickRole, setQuickRole] = useState("");
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(`Selamat datang, ${user.name}!`);
      navigate(`/${user.role}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickLogin = async (acc) => {
    setError("");
    setForm({ email: acc.email, password: acc.password });
    setQuickRole(acc.role);
    try {
      const user = await login(acc.email, acc.password);
      toast.success(`Selamat datang, ${user.name}!`);
      navigate(`/${user.role}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setQuickRole("");
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16" data-testid="login-page">
      <div className="text-center mb-8">
        <span className="inline-grid place-items-center w-12 h-12 rounded-2xl bg-primary text-primary-foreground mb-4">
          <Home size={22} />
        </span>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Masuk ke AVASA</h1>
        <p className="mt-2 text-sm text-muted-foreground">Selamat datang kembali.</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-2xl p-6 space-y-4">
        {error && (
          <div className="rounded-xl bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive" data-testid="login-error">
            {error}
          </div>
        )}
        <div>
          <label className="text-sm font-medium text-foreground">Email</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
            <Mail size={16} className="text-muted-foreground" />
            <input
              type="email"
              required
              data-testid="login-email-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="nama@email.com"
              className="flex-1 bg-transparent outline-none text-sm py-3"
            />
          </div>
        </div>
        <div>
          <label className="text-sm font-medium text-foreground">Kata Sandi</label>
          <div className="mt-1.5 flex items-center gap-2 rounded-xl bg-secondary/50 px-3 focus-within:ring-2 focus-within:ring-primary focus-within:bg-background transition-colors">
            <Lock size={16} className="text-muted-foreground" />
            <input
              type={showPw ? "text" : "password"}
              required
              data-testid="login-password-input"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="••••••••"
              className="flex-1 bg-transparent outline-none text-sm py-3"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Sembunyikan password" : "Tampilkan password"}
              data-testid="login-password-toggle"
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              {showPw ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit-button"
          className="w-full py-3 rounded-full bg-accent text-accent-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100 flex items-center justify-center gap-2"
        >
          {loading && <Loader2 size={16} className="animate-spin" />}
          {loading ? "Memproses..." : "Masuk"}
        </button>
        <p className="text-center text-sm">
          <Link to="/lupa-password" className="font-semibold text-primary hover:underline" data-testid="login-forgot-password-link">
            Lupa password?
          </Link>
        </p>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Belum punya akun?{" "}
        <Link to="/register" className="font-semibold text-primary hover:underline" data-testid="login-to-register-link">
          Daftar
        </Link>
      </p>

      {IS_PREVIEW && (
        <div className="mt-8 rounded-2xl border border-dashed border-accent/40 bg-accent/5 p-5" data-testid="demo-accounts-panel">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={16} className="text-accent" />
            <h2 className="font-heading text-base font-semibold">Akun Demo (Preview)</h2>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Klik "Login Cepat" untuk langsung masuk. Panel ini hanya tampil di lingkungan preview.
          </p>
          <div className="space-y-3">
            {DEMO_ACCOUNTS.map((acc) => {
              const Icon = acc.icon;
              const busy = quickRole === acc.role;
              return (
                <div
                  key={acc.role}
                  className="flex items-center gap-3 rounded-xl bg-card border border-border p-3"
                  data-testid={`demo-account-${acc.role}`}
                >
                  <span className="grid place-items-center w-9 h-9 shrink-0 rounded-lg bg-secondary text-foreground">
                    <Icon size={18} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold leading-tight">{acc.label}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{acc.email}</p>
                    <p className="text-[11px] text-muted-foreground truncate">Sandi: {acc.password}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleQuickLogin(acc)}
                    disabled={!!quickRole}
                    data-testid={`demo-login-${acc.role}`}
                    className="shrink-0 px-3 py-2 rounded-full bg-accent text-accent-foreground text-xs font-semibold hover:scale-[1.03] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100 flex items-center gap-1.5"
                  >
                    {busy && <Loader2 size={13} className="animate-spin" />}
                    {busy ? "Masuk..." : "Login Cepat"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
